/* Wraps the entire fleet portal. Shows a warning banner during grace, blocks access when locked.
   Data is always retained — org doc is never deleted by this gate.

   Paying is real (2026-10-28, Paystack live): the school's own plan, charged
   to the saved card or through Paystack's checkout (card or M-PESA). The old
   page offered a placeholder bank account, "card coming soon" and a mock
   button that tried to mark the school paid from the browser. */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OrgSubscription } from '../services/useOrgSubscription';
import { money as catalogMoney, BillingCurrency } from './subscriptionModel';
import {
  initializeUpgradeCheckout, previewUpgrade, upgradeNow, UpgradePreview,
} from '../services/paymentsApi';
import { SUPPORT_PHONE_LOCAL, SUPPORT_WHATSAPP_HREF } from '../utils/companyContact';
import AuthFrame, { AuthError } from '../site-v3/AuthFrame';

interface Props {
  sub: OrgSubscription;
  orgCode: string;
  orgName: string;
  children: React.ReactNode;
}

/* ── Warning banner (grace period — still usable) ────────────────────── */
export function SubscriptionWarningBanner({ sub, orgCode }: { sub: OrgSubscription; orgCode: string }) {

  if (sub.status !== 'grace') return null;
  const msg = sub.daysLeft >= 0
    ? `Your subscription expires in ${sub.daysLeft} day${sub.daysLeft === 1 ? '' : 's'}.`
    : `Your subscription expired ${Math.abs(sub.daysLeft)} day${Math.abs(sub.daysLeft) === 1 ? '' : 's'} ago.`;
  const graceMsg = sub.graceEnds
    ? ` Grace period ends ${sub.graceEnds.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })}.`
    : '';
  return (
    <div style={{ background: '#FBEFD9', borderBottom: '1px solid rgba(154,91,0,.2)', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#9A5B00', fontFamily: 'Barlow, system-ui, sans-serif' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
      <span><b>{msg}</b>{graceMsg} Pay now to keep everything running.</span>
      <a href={`/pay?org=${encodeURIComponent(orgCode)}`} style={{ marginLeft: 'auto', whiteSpace: 'nowrap', fontWeight: 700, color: '#0F1419', textDecoration: 'underline' }}>Pay now</a>
    </div>
  );
}


const PER: Record<string, string> = { monthly: 'a month', quarterly: 'a quarter', termly: 'a term', yearly: 'a year' };
const UNIT: Record<string, [string, string]> = { child: ['child', 'children'], vehicle: ['vehicle', 'vehicles'], household: ['household', 'households'] };

/** The school's plan and the one way to pay it. Shared by the locked screen
 *  and /pay ("Pay now" on Billing and in the grace banner). */
function PayCard({ orgCode, orgName, locked }: { orgCode: string; orgName: string; locked: boolean }) {
  const [q, setQ] = useState<UpgradePreview | null>(null);
  const [busy, setBusy] = useState<'card' | 'hosted' | null>(null);
  const [err, setErr] = useState('');
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    let live = true;
    if (!orgCode) { setQ({ ok: false, message: 'No organisation code.' }); return undefined; }
    previewUpgrade(orgCode).then((r) => { if (live) setQ(r); });
    return () => { live = false; };
  }, [orgCode]);

  // The catalog figure is KES; a USD school sees dollars, not "USD 136,500" (review, 2026-10-29).
  const money = (n?: number, cur?: string) => catalogMoney(n || 0, (cur === 'USD' ? 'USD' : 'KES') as BillingCurrency);
  const amount = q?.ok ? money(q.amountKES, q.currency) : '';
  const cardOffered = !!(q?.ok && q.hasSavedCard && q.isPilot);

  const hosted = async () => {
    setErr(''); setBusy('hosted');
    const r = await initializeUpgradeCheckout(orgCode);
    if (r.ok && r.authorization_url) { window.location.href = r.authorization_url; return; }
    setBusy(null); setErr(r.message || 'Could not open the payment page. Try again in a moment.');
  };
  const savedCard = async () => {
    setErr(''); setBusy('card');
    const r = await upgradeNow(orgCode);
    setBusy(null);
    if (r.ok) { setPaid(true); return; }
    setErr(r.message || 'Your card was not charged. Pay with M-PESA or another card instead.');
  };

  const btn: React.CSSProperties = { justifyContent: 'center', border: 0, font: 'inherit', fontWeight: 600, width: '100%', cursor: 'pointer' };

  if (paid) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <h1 className="d3">Paid. Thank you.</h1>
        <p className="p sm" style={{ margin: 0 }}>{orgName || 'Your school'} is active again. Paystack emails your receipt.</p>
        <a className="btn btn-k" href="/fleet" style={btn}>Open the dashboard</a>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <h1 className="d3">{locked ? 'Your account is on hold' : 'Pay for Trasse'}</h1>
      <p className="p sm" style={{ margin: 0 }}>
        {q && !q.ok
          // Nothing to charge in the app: a school Trasse bills itself, or no plan on file.
          ? <>The subscription for <b style={{ color: 'var(--ink)' }}>{orgName || orgCode}</b> {locked ? 'has ended' : 'is not paid through the app'}. Nothing is deleted.</>
          : locked
            ? <>The subscription for <b style={{ color: 'var(--ink)' }}>{orgName || orgCode}</b> has ended. Nothing is deleted — pay and everything is back straight away.</>
            : <>Pay for <b style={{ color: 'var(--ink)' }}>{orgName || orgCode}</b> now, before the plan ends.</>}
      </p>

      {!q && <div style={{ height: 120, borderRadius: 12, background: 'var(--s2)' }} aria-busy="true" />}

      {q?.ok && (
        <>
          <div style={{ border: '1px solid var(--line)', borderRadius: 12, background: 'var(--s1)', padding: '16px 18px', display: 'grid', gap: 4 }}>
            <span className="xs" style={{ color: 'var(--muted)', fontWeight: 600 }}>
              {(q.plan || '').replace(/^./, (c) => c.toUpperCase())} plan
              {q.size ? ` · ${q.size.toLocaleString('en-KE')} ${(UNIT[q.unit || 'child'] || UNIT.child)[q.size === 1 ? 0 : 1]}` : ''}
            </span>
            <span style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: 700, fontSize: 32, lineHeight: '36px', color: 'var(--ink)' }}>
              {amount}<span className="sm" style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 500, fontSize: 16, color: 'var(--muted)' }}> {PER[q.cadence || 'monthly'] || ''}</span>
            </span>
          </div>
          {/* The saved card is charged by the server's pilot upgrade only; a
              paid school that lapsed is refused there ("already on a paid
              plan"), so it goes to Paystack (review, 2026-10-29). */}
          {cardOffered && (
            <button type="button" className="btn btn-k" style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={!!busy} onClick={savedCard}>
              {busy === 'card' ? 'Charging your card…' : `Pay ${amount} with your saved card`}
            </button>
          )}
          <button type="button" className={cardOffered ? 'btn btn-s' : 'btn btn-k'} style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={!!busy} onClick={hosted}>
            {busy === 'hosted' ? 'Opening Paystack…' : cardOffered ? 'Pay with M-PESA or another card' : `Pay ${amount}`}
          </button>
          <p className="xs" style={{ margin: 0, color: 'var(--muted)' }}>
            Paystack takes the payment: card or M-PESA. Your account updates as soon as it clears.
          </p>
        </>
      )}

      {q && !q.ok && (
        <p className="p sm" style={{ margin: 0 }}>
          We could not find a plan to charge for this account. Call or WhatsApp us on{' '}
          <a className="lnk" href={SUPPORT_WHATSAPP_HREF} target="_blank" rel="noreferrer">{SUPPORT_PHONE_LOCAL}</a> and we will sort it out.
        </p>
      )}
      {err && <AuthError>{err}</AuthError>}
    </div>
  );
}

/* ── Lockout screen (locked — no access) ─────────────────────────────── */
export function SubscriptionLockout({ orgCode, orgName }: { sub: OrgSubscription; orgCode: string; orgName: string }) {
  const { logout } = useAuth();
  return (
    <AuthFrame
      foot={(
        <p className="xs" style={{ margin: 0, color: 'var(--muted)' }}>
          Questions? <a className="lnk" href={SUPPORT_WHATSAPP_HREF} target="_blank" rel="noreferrer">WhatsApp {SUPPORT_PHONE_LOCAL}</a>
          {' · '}
          <a className="lnk" href="/sign-in" onClick={(e) => { e.preventDefault(); logout().finally(() => { window.location.href = '/sign-in'; }); }}>Sign out</a>
        </p>
      )}
    >
      <PayCard orgCode={orgCode} orgName={orgName} locked />
    </AuthFrame>
  );
}

/* ── /pay — "Pay now" from Billing and the grace banner ──────────────── */
export function PayPage() {
  const { currentUser, authReady, fleetOrgCode } = useAuth();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const orgCode = params.get('org') || fleetOrgCode || '';
  if (!authReady) return <div style={{ minHeight: '100vh' }} aria-busy="true" />;
  // Paying is for the school's admin: the server checks they run this org.
  if (!currentUser) return <Navigate to={`/sign-in?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return (
    <AuthFrame
      foot={(
        <p className="xs" style={{ margin: 0, color: 'var(--muted)' }}>
          <a className="lnk" href="/fleet/billing">Back to Plan and billing</a>
        </p>
      )}
    >
      <PayCard orgCode={orgCode} orgName="" locked={false} />
    </AuthFrame>
  );
}

/* ── Main gate wrapper ───────────────────────────────────────────────── */
export default function SubscriptionGate({ sub, orgCode, orgName, children }: Props) {
  if (sub.status === 'loading') return null;
  if (sub.status === 'locked') return <SubscriptionLockout sub={sub} orgCode={orgCode} orgName={orgName} />;
  return (
    <>
      <SubscriptionWarningBanner sub={sub} orgCode={orgCode} />
      {children}
    </>
  );
}
